/**
 * healthcareImport: the HEALTHCARE dialect of the shared catalogue importer.
 * ───────────────────────────────────────────────────────────────────────────
 * The parsing, the validation and every rule that is not pharmacy-specific
 * live in catalogueImport.js, the same code the brand importer runs on. What
 * is left here is what only a regulated seller has: product or service, the
 * prescription and age gates, the controlled-substance flag and its schedule,
 * the VAT treatment, the batch and expiry register, and the slot count a
 * service is booked against.
 *
 * Founder decision, 27 Sep 2026: a pharmacy sells both in bulk and at retail,
 * so it behaves like a brand commercially. That is why Min Order Qty and Price
 * on Request are read from the SHARED columns rather than re-declared here.
 *
 * NO TIER PRICES, AND THAT IS THE POINT. See the long note in
 * healthcareImportColumns.js: the healthcare pricing phase is not tier-aware,
 * so a `tierPrices` object on a pharmacy product is silently re-priced at the
 * retail base. This file never parses one, never validates one and never
 * builds one, and healthcareImport.test.js fails if a tier column ever appears.
 *
 * NO SPEC COLUMNS. The healthcare catalogue has no `specs` field, so
 * `supportsSpecs` is false and a `spec:` header is left as an unknown column
 * rather than parsed into something the server would drop.
 *
 * THREE RULES INHERITED FROM THE SHARED LAYER, all of which a pharmacy needs
 * at least as much as a brand:
 *
 *   1. AN AVAILABILITY GATE. An out-of-stock row is SKIPPED with a stated
 *      reason. The create path coerces `countInStock || 1`, so importing one
 *      would list a medicine that is not on the shelf as having one in stock.
 *   2. DUPLICATE DETECTION. A row whose Barcode (or section + name) is already
 *      on this pharmacy is marked "already imported" and skipped, so a partial
 *      import is resumed by re-running the same workbook.
 *   3. THE ZERO-PRICE FLOOR. A price of 0 is an error unless Price on Request
 *      is Yes. Founder rule, 29 Sep 2026: no item may sell at 0.
 */
import {
  PRODUCT_TYPES,
  VAT_CATEGORIES,
  canonicalHeader,
  labelOf,
} from "./healthcareImportColumns.js";
import { createCatalogueImport } from "./catalogueImport.js";

export {
  MAX_ADDITIONAL_IMAGES,
  PAYMENT_METHODS,
  parseBool,
  parseList,
  parseMoney,
  parseStatus,
  readAvailability,
} from "./catalogueImport.js";
export { PRODUCT_TYPES, VAT_CATEGORIES } from "./healthcareImportColumns.js";

/**
 * One Batches cell → the entries the operator wrote, unvalidated.
 *
 * Split on semicolons and newlines but NOT on commas, unlike every other list
 * column on the sheet: a batch number is exactly the sort of string a supplier
 * puts a comma in, and a quantity pasted out of a stock system arrives as
 * "1,200". Splitting on commas here would turn one batch into three fragments
 * and then report all three as unreadable.
 *
 * Fields are separated by a pipe: number | expiry | quantity. Returns the raw
 * pieces; deciding whether they are usable is validateExtras' job, so the
 * review table can say which entry is wrong and why.
 */
export const parseBatchCell = (v) => {
  if (v === null || v === undefined || v === "") return [];
  return String(v)
    .split(/[;\n]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((entry) => {
      const [batchNumber = "", expiry = "", quantity = ""] = entry
        .split("|")
        .map((s) => s.trim());
      return { raw: entry, batchNumber, expiry, quantity };
    });
};

/**
 * "2027-03-31" → a Date, or null.
 *
 * Accepts what an operator actually types: an ISO date, a date Excel handed
 * back as a real Date object, and an Excel serial number (a cell formatted as
 * a date arrives as 46477 when the sheet was written by a stock system that
 * did not set a number format). Anything else is null and becomes an error,
 * because an expiry nobody can read is worse than no batch at all: the counter
 * prints it on the receipt and the terminal refuses the sale on it.
 */
export const parseExpiry = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === "number" && Number.isFinite(v)) {
    // Excel's day 0 is 30 Dec 1899; its leap-year bug is already in that epoch.
    const d = new Date(Date.UTC(1899, 11, 30) + v * 86400000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(v).trim();
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** A Date → the "YYYY-MM-DD" string the POS wire contract expects. */
export const toWireDate = (d) => d.toISOString().slice(0, 10);

const dialect = {
  canonicalHeader,
  labelOf,
  vendorNoun: "pharmacy",
  /* The healthcare catalogue has no `specs` field. See the file header. */
  supportsSpecs: false,

  readExtras: (r, { parseMoney, parseBool, enumValue, str }) => ({
    productType: enumValue(r.product_type),
    vatCategory: enumValue(r.vat_category),
    requiresPrescription: parseBool(r.requires_prescription, false),
    ageRestriction: parseMoney(r.age_restriction),
    controlledSubstance: parseBool(r.controlled_substance, false),
    controlledSchedule: str(r.controlled_schedule),
    availableSlots: parseMoney(r.available_slots),
    batchEntries: parseBatchCell(r.batches),
  }),

  validateExtras: ({ item, errors, warnings, P }) => {
    // ── Product or service ─────────────────────────────────────────────────
    const productType = item.productType || "PRODUCT";
    if (!PRODUCT_TYPES.includes(productType)) {
      errors.push(
        `${P("product_type")} "${item.productType}" is not one of ${PRODUCT_TYPES.join(" / ")}`
      );
    }
    const isService = productType === "SERVICE";

    /* The two stock columns are mutually exclusive and the server enforces it
       with a conditional `required`. Warn rather than fail: filling both is an
       operator being thorough, and the one that does not apply is simply not
       sent. Silently accepting it is what would be wrong, because the operator
       would then believe a number is live that nothing reads. */
    if (isService && item.countInStock !== null) {
      warnings.push(
        `${P("count_in_stock")} is only read for a Product, so it will not be sent for this Service`
      );
    }
    if (!isService && item.availableSlots !== null) {
      warnings.push(
        `${P("available_slots")} is only read for a Service, so it will not be sent for this Product`
      );
    }
    if (item.availableSlots !== null && item.availableSlots < 0) {
      errors.push(`${P("available_slots")} cannot be negative`);
    }

    // ── Fiscal ─────────────────────────────────────────────────────────────
    const vatCategory = item.vatCategory || "";
    if (vatCategory && !VAT_CATEGORIES.includes(vatCategory)) {
      errors.push(
        `${P("vat_category")} "${item.vatCategory}" is not one of ${VAT_CATEGORIES.join(" / ")}`
      );
    }

    // ── Regulation ─────────────────────────────────────────────────────────
    const ageRestriction = item.ageRestriction === null ? 0 : item.ageRestriction;
    if (ageRestriction < 0) {
      errors.push(`${P("age_restriction")} cannot be negative`);
    } else if (!Number.isInteger(ageRestriction)) {
      warnings.push(`${P("age_restriction")} ${ageRestriction} will be rounded to a whole year`);
    }

    /* The flag and the schedule have to agree, and neither is inferred from
       the other. A schedule with the flag off means the pharmacist filled in
       the regulator's classification and forgot to tick the box, which leaves
       the product outside the controlled-substance register it belongs in. A
       flag with no schedule is legitimate in a market whose regulator does not
       schedule, so it is only worth a note. */
    if (item.controlledSchedule && !item.controlledSubstance) {
      warnings.push(
        `${P("controlled_schedule")} is filled in but ${P("controlled_substance")} is not Yes, so this product will not be treated as controlled`
      );
    }
    if (item.controlledSubstance && !item.controlledSchedule) {
      warnings.push(
        `${P("controlled_substance")} is Yes with no ${P("controlled_schedule")}, so nothing will print on the controlled-substance log`
      );
    }

    // ── Batch register ─────────────────────────────────────────────────────
    /* A batch the server cannot read is DROPPED by sanitiseProductBatches
       without a word, so a malformed entry here is an error rather than a
       warning. Importing a medicine whose expiry silently vanished is how a
       counter dispenses an expired box: the terminal refuses a sale on an
       expired batch, and it can only do that for a batch it was given. */
    const batches = [];
    const now = Date.now();
    for (const entry of item.batchEntries) {
      if (!entry.batchNumber) {
        errors.push(`${P("batches")}: "${entry.raw}" has no batch number`);
        continue;
      }
      const expiry = parseExpiry(entry.expiry);
      if (!expiry) {
        errors.push(
          `${P("batches")}: "${entry.raw}" has no readable expiry date (write it as ${entry.batchNumber} | 2027-03-31 | 120)`
        );
        continue;
      }
      const quantityRaw = String(entry.quantity).replace(/[^0-9.-]/g, "");
      const quantity = quantityRaw === "" ? 0 : Number(quantityRaw);
      if (!Number.isFinite(quantity) || quantity < 0) {
        errors.push(`${P("batches")}: "${entry.raw}" has an unreadable quantity`);
        continue;
      }
      if (expiry.getTime() < now) {
        warnings.push(
          `${P("batches")}: batch ${entry.batchNumber} expired on ${toWireDate(expiry)}; it will be imported so the counter can see it, and the terminal will refuse to sell it`
        );
      }
      batches.push({
        batchNumber: entry.batchNumber,
        expiryDate: toWireDate(expiry),
        quantity: Math.round(quantity),
      });
    }

    return {
      productType,
      isService,
      vatCategory,
      ageRestriction: Math.round(ageRestriction),
      batches,
    };
  },
};

const importer = createCatalogueImport(dialect);

export const findHeaderRow = importer.findHeaderRow;
export const pickSheet = importer.pickSheet;
export const parseWorkbook = importer.parseWorkbook;

/**
 * @param {object} parsed        output of parseWorkbook
 * @param {File[]} imageFiles    photos the operator picked
 * @param {object} ctx
 * @param {Array}  ctx.existingSections   GET /healthcare/healthcare-sections
 * @param {Array}  ctx.platformCategories [{_id, category}] from /category/get-all
 * @param {Array}  ctx.units              [{_id, unit}] from /unit
 */
export const validateImport = importer.validateImport;

export const buildImportPlan = importer.buildImportPlan;

export default buildImportPlan;
